import { z } from 'zod';

import { tenantStatusSchema } from '../domain/types';
import { nitSchema } from './nit';

/**
 * Billing cycles a contract may use.
 */
export const billingCycleSchema = z.enum(['monthly', 'annual']);

/**
 * A billing cycle value.
 */
export type BillingCycle = z.infer<typeof billingCycleSchema>;

/**
 * Lifecycle of a contract.
 */
export const contractStatusSchema = z.enum(['active', 'ended']);

/**
 * A contract status value.
 */
export type ContractStatus = z.infer<typeof contractStatusSchema>;

const money = z.number().min(0).max(1_000_000_000);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

/**
 * What ClassGO staff provide to create a school.
 */
export const createTenantInputSchema = z.object({
  name: z.string().min(2).max(160),
  legalName: z.string().min(2).max(200).optional(),
  nit: nitSchema.optional(),
  daneCode: z.string().min(3).max(32).optional(),
  countryCode: z.string().length(2).default('CO'),
  timezone: z.string().min(3).default('America/Bogota'),
  currency: z.string().length(3).default('COP'),
  locale: z.string().min(2).default('es-CO'),
  isDemo: z.boolean().default(false),
  mainSedeName: z.string().min(1).max(120).default('Principal'),
});

/**
 * Input of `POST /staff/tenants`.
 */
export type CreateTenantInput = z.input<typeof createTenantInputSchema>;

/**
 * Editable tenant fields.
 */
export const updateTenantInputSchema = z
  .object({
    name: z.string().min(2).max(160),
    legalName: z.string().min(2).max(200).nullable(),
    nit: nitSchema.nullable(),
    daneCode: z.string().min(3).max(32).nullable(),
    countryCode: z.string().length(2),
    timezone: z.string().min(3),
    currency: z.string().length(3),
    locale: z.string().min(2),
    status: tenantStatusSchema,
    isDemo: z.boolean(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to update');

/**
 * Input of `PATCH /staff/tenants/{id}`.
 */
export type UpdateTenantInput = z.input<typeof updateTenantInputSchema>;

/**
 * Commercial terms for a school.
 */
export const createContractInputSchema = z
  .object({
    startsOn: isoDate,
    endsOn: isoDate.optional(),
    currency: z.string().length(3).default('COP'),
    billingCycle: billingCycleSchema.default('monthly'),
    platformFee: money.default(0),
    pricePerStudent: money.default(0),
    discountPercent: z.number().min(0).max(100).default(0),
    courtesyStartsOn: isoDate.optional(),
    courtesyEndsOn: isoDate.optional(),
    mentoriaEnabled: z.boolean().default(false),
    mentoriaPricePerStudent: money.optional(),
    mentoriaMonthlyQuota: z.number().int().min(1).max(1000).optional(),
    mentoriaModelId: z.string().max(120).optional(),
    mentoriaFamilyPrice: money.optional(),
    notes: z.string().max(2000).optional(),
  })
  .refine(
    (value) =>
      !value.courtesyStartsOn ||
      !value.courtesyEndsOn ||
      value.courtesyStartsOn <= value.courtesyEndsOn,
    { message: 'courtesyEndsOn must not precede courtesyStartsOn', path: ['courtesyEndsOn'] },
  );

/**
 * Input of `POST /staff/tenants/{id}/contracts`.
 */
export type CreateContractInput = z.input<typeof createContractInputSchema>;

/**
 * Editable contract fields.
 */
export const updateContractInputSchema = z
  .object({
    status: contractStatusSchema,
    endsOn: isoDate.nullable(),
    billingCycle: billingCycleSchema,
    platformFee: money,
    pricePerStudent: money,
    discountPercent: z.number().min(0).max(100),
    courtesyStartsOn: isoDate.nullable(),
    courtesyEndsOn: isoDate.nullable(),
    mentoriaEnabled: z.boolean(),
    mentoriaPricePerStudent: money.nullable(),
    /**
     * MentorIA per contract (#111): questions per student per month (null: the environment's
     * default), the Bedrock model (null: MentorIA's default) and what the school charges each
     * family per month (null: the price per student; 0: the school absorbs it).
     */
    mentoriaMonthlyQuota: z.number().int().min(1).max(1000).nullable(),
    mentoriaModelId: z.string().max(120).nullable(),
    mentoriaFamilyPrice: money.nullable(),
    notes: z.string().max(2000).nullable(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to update');

/**
 * Input of `PATCH /staff/tenants/{id}/contracts/{contractId}`.
 */
export type UpdateContractInput = z.input<typeof updateContractInputSchema>;

/**
 * Who becomes the school owner.
 */
export const createOwnerInputSchema = z.object({
  email: z.string().email().max(254),
  givenName: z.string().min(1).max(80).optional(),
  familyName: z.string().min(1).max(80).optional(),
});

/**
 * Input of `POST /staff/tenants/{id}/owners`.
 */
export type CreateOwnerInput = z.input<typeof createOwnerInputSchema>;

/**
 * The names of a school owner, as the console corrects them. The email is the sign-in name and
 * cannot change.
 */
export const updateOwnerInputSchema = z.object({
  givenName: z.string().trim().min(1).max(80),
  familyName: z.string().trim().min(1).max(80),
});

/**
 * Input of `PATCH /staff/tenants/{id}/owners/{userId}`.
 */
export type UpdateOwnerInput = z.input<typeof updateOwnerInputSchema>;

/**
 * A new ClassGO staff member.
 */
export const createStaffUserInputSchema = z.object({
  email: z.string().email().max(254),
  givenName: z.string().min(1).max(80).optional(),
  familyName: z.string().min(1).max(80).optional(),
  role: z.enum(['STAFF_ADMIN', 'SUPER_ADMIN']).default('STAFF_ADMIN'),
});

/**
 * Input of `POST /staff/users`.
 */
export type CreateStaffUserInput = z.input<typeof createStaffUserInputSchema>;

/**
 * Why a staff member enters a school.
 */
export const actAsInputSchema = z.object({
  reason: z.string().min(3).max(300),
});

/**
 * Input of `POST /staff/tenants/{id}/act-as`.
 */
export type ActAsInput = z.input<typeof actAsInputSchema>;

/**
 * Header a staff member sends while acting inside a school.
 */
export const ACT_AS_HEADER = 'x-act-as-session';
